import { authHeader, getToken, clearToken } from "./auth";

const BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeader(), ...init?.headers },
  });
  if (!res.ok) {
    // An expired/invalid JWT on an authenticated request: drop the dead token
    // and bounce to login so the user isn't left staring at a cryptic error.
    // Guarded by getToken() so a 401 on the login form (wrong password, no
    // token yet) keeps its error message instead of redirecting.
    if (res.status === 401 && getToken() && typeof window !== "undefined") {
      clearToken();
      window.location.href = "/login";
    }
    throw new Error(await res.text());
  }
  return res.json();
}

// Errors thrown by `request` carry the raw response body as their message. Our
// backend returns FastAPI `detail` either as a plain string or as a structured
// {code, message} object. This normalises both so callers can branch on `code`
// (e.g. "invalid_api_key") and always have a human-readable `message`.
export function parseApiError(e: unknown): { code: string; message: string } {
  const raw = e instanceof Error ? e.message : String(e ?? "");
  try {
    const parsed = JSON.parse(raw);
    const detail = parsed?.detail;
    if (detail && typeof detail === "object") {
      return { code: detail.code ?? "", message: detail.message ?? raw };
    }
    return { code: "", message: typeof detail === "string" ? detail : raw };
  } catch {
    return { code: "", message: raw };
  }
}

export type Case = { id: number; name: string; age: number; presenting_complaint: string; presenting_complaint_tr?: string | null; difficulty_level: string; is_custom?: boolean };
export type CueItem = { cue: string; significance: string; easy_to_miss_because: string };
export type CreateCaseRequest = {
  name: string; age: number; presenting_complaint: string;
  difficulty_level: string; default_mode: string;
  surface_presentation?: string; underlying_issues?: string;
  personality_and_voice?: string; silence_behavior?: string;
  opens_up_when?: string[]; shuts_down_when?: string[]; tests_therapist_by?: string[];
  cue_inventory?: CueItem[];
};
export type TranscriptMessage = { role: "user" | "assistant"; content: string; timestamp: string };
export type Session = { id: number; patient_case_id: number; started_at: string; ended_at: string | null; supervisor_report: unknown; reset_cycle: number; transcript?: TranscriptMessage[] | null };
export type TokenResponse = { access_token: string; user_id: number; name: string };
export type SubscriptionStatus = {
  tier: "trial" | "pro" | "basic" | "free" | "tester";
  sessions_used_this_month: number | null;
  sessions_limit: number | null;
  trial_ends_at: string | null;
  days_remaining: number | null;
  trial_sessions_used: number | null;
  trial_sessions_remaining: number | null;
};
// Public plan catalog for the pricing page. monthly_price is in EUR cents.
export type Plan = { name: string; monthly_price: number; limits: Record<string, number | boolean> };
export type Currency = "EUR" | "USD";
export type BillingInterval = "monthly" | "yearly";
// iyzico checkout (shadow). When the integration is off, `enabled` is false and
// the pricing page keeps its "coming soon" behaviour.
export type CheckoutResponse = {
  enabled: boolean;
  detail?: string;
  token?: string;
  checkoutFormContent?: string;
  tokenExpireTime?: number;
};
export type CheckoutResult = {
  enabled: boolean;
  status?: string;
  tier?: string | null;
  current_period_end?: string | null;
  detail?: string;
};
// Invoice/billing details captured on the payment screen. `country_code` (ISO-2)
// is what tells a domestic (TR) sale from a tax-exempt export.
export type BillingProfile = {
  buyer_type: "individual" | "company";
  full_name: string;
  company_name: string;
  country: string;
  country_code: string;
  city: string;
  address: string;
  zip_code: string;
  phone: string;
  identity_number: string;
  tax_office: string;
  tax_id: string;
  email: string;
};
export type SkillKey = "empathy" | "reflection" | "question_quality" | "focus" | "rapport";
export type ProgressPoint = {
  session_id: number;
  case_id: number;
  case_name: string;
  ended_at: string | null;
  overall: number;
  skills: Partial<Record<SkillKey, number>>;
};
export type PatientProgress = {
  case_id: number;
  name: string;
  age: number;
  alliance: number;
  session_count: number;
  insights: number;
  progress_map: Record<string, string>;
  wellbeing: number | null;
  wellbeing_trend: { value: number; ended_at: string | null }[];
};
export type Progress = {
  overall: {
    trend: ProgressPoint[];
    early_avg: Partial<Record<SkillKey, number>> | null;
    recent_avg: Partial<Record<SkillKey, number>> | null;
    latest_overall: number | null;
    band: "beginning" | "developing" | "competent" | "strong" | "exceptional" | null;
  };
  stats: {
    evaluated_sessions: number;
    patients_count: number;
    practice_minutes: number;
    best_skill: SkillKey | null;
    weakest_skill: SkillKey | null;
  };
  patients: PatientProgress[];
};
export type NotificationPrefs = { session_feedback: boolean; product_updates: boolean };
// "Invite a Friend" — the user's referral code + shareable link + reward stats.
export type Referral = {
  code: string;
  link: string;
  invited_count: number;
  rewarded_count: number;
  bonus_active: boolean;
  bonus_pro_until: string | null;
  reward_days: number;
};
export type GuideSection = { heading: string; body: string; bullets: string[] };
export type GuideContent = { title: string; intro: string; sections: GuideSection[]; footer: string };
export type Guide = { locale: string; content: GuideContent; source: string; generated_at: string | null };

export const api = {
  register: (email: string, name: string, password: string, anthropic_api_key?: string, locale?: string, referral_code?: string) =>
    request<TokenResponse>("/auth/register", { method: "POST", body: JSON.stringify({ email, name, password, anthropic_api_key, locale, referral_code }) }),

  updateApiKey: (anthropic_api_key: string) =>
    request<{ ok: boolean }>("/auth/api-key", { method: "PUT", body: JSON.stringify({ anthropic_api_key }) }),

  deleteApiKey: () =>
    request<{ ok: boolean }>("/auth/api-key", { method: "DELETE" }),

  changePassword: (current_password: string, new_password: string) =>
    request<{ ok: boolean }>("/auth/password", { method: "PUT", body: JSON.stringify({ current_password, new_password }) }),

  getMe: () =>
    request<{ id: number; name: string; email: string; has_api_key: boolean; api_key_preview: string | null; consent_accepted: boolean; email_verified: boolean }>("/auth/me"),

  verifyEmail: (code: string) =>
    request<{ ok: boolean; already_verified?: boolean }>("/auth/verify-email", { method: "POST", body: JSON.stringify({ code }) }),

  resendVerification: (locale?: string) =>
    request<{ ok: boolean; already_verified?: boolean }>("/auth/verify-email/resend", { method: "POST", body: JSON.stringify({ locale }) }),

  forgotPassword: (email: string, locale?: string) =>
    request<{ ok: boolean }>("/auth/password/forgot", { method: "POST", body: JSON.stringify({ email, locale }) }),

  resetPassword: (token: string, new_password: string) =>
    request<{ ok: boolean }>("/auth/password/reset", { method: "POST", body: JSON.stringify({ token, new_password }) }),

  acceptConsent: (locale?: string, marketing?: boolean) =>
    request<{ ok: boolean }>("/auth/consent/accept", { method: "POST", body: JSON.stringify({ locale, marketing }) }),

  declineConsent: () =>
    request<{ ok: boolean }>("/auth/consent/decline", { method: "POST" }),

  requestAccountDeletion: (locale?: string) =>
    request<{ ok: boolean }>("/auth/account/delete/request", { method: "POST", body: JSON.stringify({ locale }) }),

  confirmAccountDeletion: (code: string) =>
    request<{ ok: boolean }>("/auth/account/delete/confirm", { method: "POST", body: JSON.stringify({ code }) }),

  login: (email: string, password: string) =>
    request<TokenResponse>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),

  googleAuth: (credential: string, referral_code?: string) =>
    request<TokenResponse>("/auth/google", { method: "POST", body: JSON.stringify({ credential, referral_code }) }),

  getReferral: () => request<Referral>("/referral"),

  getCases: () => request<Case[]>("/cases"),

  getGuide: (locale: string) => request<Guide>(`/guide?locale=${encodeURIComponent(locale)}`),

  startSession: (caseId: number) =>
    request<Session>("/sessions", { method: "POST", body: JSON.stringify({ patient_case_id: caseId }) }),

  sendMessage: (sessionId: number, content: string) =>
    request<{ role: string; content: string; timestamp: string; session_ended_by_patient: boolean; session_ended_by_limit: boolean; student_message_count: number; message_cap: number }>(`/sessions/${sessionId}/message`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  endSession: (sessionId: number) =>
    request<Session>(`/sessions/${sessionId}/end`, { method: "POST" }),

  getSession: (sessionId: number) =>
    request<Session>(`/sessions/${sessionId}`),

  evaluateSession: (sessionId: number, locale = "en") =>
    request<Session>(`/sessions/${sessionId}/evaluate?language=${encodeURIComponent(locale)}`, { method: "POST" }),

  getCaseState: (caseId: number) =>
    request<{ session_count: number }>(`/cases/${caseId}/state`),

  resetCaseState: (caseId: number) =>
    request<{ ok: boolean }>(`/cases/${caseId}/reset-state`, { method: "POST" }),

  getSessionsByCase: (caseId: number) =>
    request<Session[]>(`/sessions/by-case/${caseId}`),

  getProgress: () => request<Progress>("/sessions/progress"),

  getSubscriptionStatus: () =>
    request<SubscriptionStatus>("/subscription/status"),

  getPlans: () =>
    request<Plan[]>("/subscription/plans"),

  startCheckout: (plan: string, currency: Currency = "EUR", interval: BillingInterval = "monthly") =>
    request<CheckoutResponse>("/payments/subscription/checkout", {
      method: "POST",
      body: JSON.stringify({ plan, currency, interval }),
    }),

  getCheckoutResult: (token: string) =>
    request<CheckoutResult>("/payments/subscription/checkout/result", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  cancelSubscription: () =>
    request<{ enabled: boolean; status?: string }>("/payments/subscription/cancel", { method: "POST" }),

  getBillingProfile: () =>
    request<BillingProfile>("/payments/billing-profile"),

  saveBillingProfile: (profile: Partial<BillingProfile>) =>
    request<BillingProfile>("/payments/billing-profile", { method: "PUT", body: JSON.stringify(profile) }),

  createCase: (body: CreateCaseRequest) =>
    request<Case>("/cases", { method: "POST", body: JSON.stringify(body) }),

  getNotificationPrefs: () =>
    request<NotificationPrefs>("/notifications/preferences"),

  updateNotificationPrefs: (prefs: NotificationPrefs) =>
    request<NotificationPrefs>("/notifications/preferences", { method: "PUT", body: JSON.stringify(prefs) }),
};
